import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Trash2, X, Users, Check, ChevronRight, Edit2 } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  createProgram,
  updateProgram,
  fetchProgramDetail,
  addActivity,
  updateActivity,
  deleteActivity,
  reorderActivities,
  fetchProgramAssignments,
  assignProgram,
  unassignProgram,
  fetchCoachRespondentsForAssignment,
  fetchPartnerCoachesForAssignment,
} from '../../lib/programs';
import { ACTIVITY_TYPES, ACTIVITY_TYPE_LABELS } from '../../types/program';
import type { ProgramDetail, ProgramActivity, ActivityType, AssigneeOption } from '../../types/program';
import { supabase } from '../../lib/supabase';

interface Props {
  creatorType: 'coach' | 'partner';
  basePath: string;
}

export function ProgramBuilder({ creatorType, basePath }: Props) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSequential, setIsSequential] = useState(false);
  const [isTemplate, setIsTemplate] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [activities, setActivities] = useState<ProgramActivity[]>([]);
  const [programId, setProgramId] = useState<string | null>(id || null);

  // Activity editor state
  const [editingActivity, setEditingActivity] = useState<Partial<ProgramActivity> | null>(null);
  const [editingIndex, setEditingIndex] = useState<number>(-1);

  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignees, setAssignees] = useState<AssigneeOption[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<Set<string>>(new Set());
  const [currentAssignments, setCurrentAssignments] = useState<{ assigneeUserId: string; assigneeName: string; assigneeEmail: string }[]>([]);

  const loadProgram = useCallback(async () => {
    if (!id) return;
    const detail = await fetchProgramDetail(id);
    if (!detail) { navigate(basePath); return; }
    setTitle(detail.title);
    setDescription(detail.description);
    setIsSequential(detail.isSequential);
    setIsTemplate(detail.isTemplate);
    setIsPublished(detail.isPublished);
    setActivities(detail.activities);

    const assignments = await fetchProgramAssignments(id);
    setCurrentAssignments(assignments.map(a => ({ assigneeUserId: a.assigneeUserId, assigneeName: a.assigneeName || 'Unknown', assigneeEmail: a.assigneeEmail || '' })));
  }, [id, navigate, basePath]);

  useEffect(() => {
    if (isEdit) {
      loadProgram().finally(() => setLoading(false));
    }
  }, [isEdit, loadProgram]);

  const handleSaveProgram = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      if (programId) {
        await updateProgram(programId, { title, description, isSequential, isTemplate, isPublished });
      } else {
        const newId = await createProgram(user.id, creatorType, { title, description, isSequential, isTemplate });
        setProgramId(newId);
        navigate(`${basePath}/${newId}/edit`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving program:', err);
      alert('Could not save the program. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const ensureProgramSaved = async (): Promise<string | null> => {
    if (programId) return programId;
    if (!user?.id) return null;
    if (!title.trim()) {
      alert('Please enter a program title before adding activities.');
      return null;
    }
    setSaving(true);
    try {
      const newId = await createProgram(user.id, creatorType, { title, description, isSequential, isTemplate });
      setProgramId(newId);
      navigate(`${basePath}/${newId}/edit`, { replace: true });
      return newId;
    } catch (err) {
      console.error('Error saving program:', err);
      alert('Could not save the program. Please try again.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleAddActivity = async () => {
    const savedId = await ensureProgramSaved();
    if (!savedId) return;
    setEditingActivity({
      activityType: 'task',
      title: '',
      instructions: '',
      resourceUrl: '',
      dueDate: null,
      position: activities.length,
    });
    setEditingIndex(-1);
  };

  const handleEditActivity = (activity: ProgramActivity, index: number) => {
    setEditingActivity({ ...activity });
    setEditingIndex(index);
  };

  const handleSaveActivity = async () => {
    if (!editingActivity || !programId) return;
    if (!editingActivity.title?.trim()) { alert('Please enter a title for this activity.'); return; }

    try {
      if (editingIndex >= 0 && editingActivity.id) {
        await updateActivity(editingActivity.id, {
          activityType: editingActivity.activityType as ActivityType,
          title: editingActivity.title,
          instructions: editingActivity.instructions || '',
          resourceUrl: editingActivity.resourceUrl || '',
          dueDate: editingActivity.dueDate || null,
        });
        setActivities(prev => prev.map((a, i) => i === editingIndex ? { ...a, ...editingActivity } as ProgramActivity : a));
      } else {
        const newId = await addActivity(programId, {
          activityType: editingActivity.activityType as ActivityType,
          title: editingActivity.title,
          instructions: editingActivity.instructions || '',
          resourceUrl: editingActivity.resourceUrl || '',
          dueDate: editingActivity.dueDate || null,
          position: activities.length,
        });
        setActivities(prev => [...prev, { ...editingActivity, id: newId, programId } as ProgramActivity]);
      }
      setEditingActivity(null);
      setEditingIndex(-1);
    } catch (err) {
      console.error('Error saving activity:', err);
      alert('Could not save the activity. Please check your connection and try again.');
    }
  };

  const handleDeleteActivity = async (activityId: string, index: number) => {
    if (!confirm('Delete this activity?')) return;
    try {
      await deleteActivity(activityId);
      const newActivities = activities.filter((_, i) => i !== index);
      setActivities(newActivities);
      if (programId) {
        await reorderActivities(programId, newActivities.map(a => a.id));
      }
    } catch (err) {
      console.error('Error deleting activity:', err);
    }
  };

  const handleMoveActivity = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= activities.length) return;
    const newActivities = [...activities];
    [newActivities[index], newActivities[newIndex]] = [newActivities[newIndex], newActivities[index]];
    newActivities.forEach((a, i) => a.position = i);
    setActivities(newActivities);
    if (programId) {
      await reorderActivities(programId, newActivities.map(a => a.id));
    }
  };

  const loadAssignees = async () => {
    if (!user?.id) return;
    let options: AssigneeOption[] = [];
    if (creatorType === 'coach') {
      options = await fetchCoachRespondentsForAssignment(user.id);
    } else {
      options = await fetchPartnerCoachesForAssignment(user.id);
    }
    setAssignees(options);
    const alreadyAssigned = new Set(currentAssignments.map(a => a.assigneeUserId));
    setSelectedAssignees(alreadyAssigned);
  };

  const handleToggleAssignee = (userId: string) => {
    setSelectedAssignees(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSaveAssignments = async () => {
    if (!programId) return;
    const currentSet = new Set(currentAssignments.map(a => a.assigneeUserId));

    const toAdd: string[] = [];
    const toRemove: string[] = [];
    selectedAssignees.forEach(uid => { if (!currentSet.has(uid)) toAdd.push(uid); });
    currentSet.forEach(uid => { if (!selectedAssignees.has(uid)) toRemove.push(uid); });

    try {
      for (const uid of toAdd) {
        await assignProgram(programId, uid, creatorType === 'coach' ? 'respondent' : 'coach');
      }
      for (const uid of toRemove) {
        await unassignProgram(programId, uid);
      }

      const updated = await fetchProgramAssignments(programId);
      setCurrentAssignments(updated.map(a => ({ assigneeUserId: a.assigneeUserId, assigneeName: a.assigneeName || 'Unknown', assigneeEmail: a.assigneeEmail || '' })));

      if (toAdd.length > 0 && title.trim()) {
        const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
        const programUrl = creatorType === 'coach'
          ? `${siteUrl}/respondent/programs`
          : `${siteUrl}/coach/programs`;
        const assignerName = user?.user_metadata?.full_name || user?.email || (creatorType === 'coach' ? 'Your coach' : 'Your partner');

        const addedAssignees = updated.filter(a => toAdd.includes(a.assigneeUserId));
        const { data: { session } } = await supabase.auth.getSession();

        for (const assignee of addedAssignees) {
          if (!assignee.assigneeEmail) continue;
          try {
            await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
              },
              body: JSON.stringify({
                type: 'program-assigned',
                recipientEmail: assignee.assigneeEmail,
                recipientName: assignee.assigneeName || 'there',
                assignerName,
                programTitle: title,
                programUrl,
              }),
            });
          } catch (emailErr) {
            console.error('Failed to send program-assigned email:', emailErr);
          }
        }
      }

      setShowAssignModal(false);
    } catch (err) {
      console.error('Error updating assignments:', err);
      alert('Failed to update assignments.');
    }
  };

  if (loading) return <LoadingSpinner message="Loading program..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(basePath)} className="text-chocolate-400 hover:text-chocolate-700 text-sm">
              ← Programs
            </button>
            <span className="text-chocolate-300">/</span>
            <h1 className="freud-heading-lg text-chocolate-900">{isEdit ? 'Edit Program' : 'New Program'}</h1>
          </div>
          <div className="flex items-center gap-2">
            {programId && (
              <button
                onClick={() => { loadAssignees(); setShowAssignModal(true); }}
                className="freud-btn-secondary inline-flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Assign
              </button>
            )}
            <button
              onClick={handleSaveProgram}
              disabled={saving}
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Program Details */}
        <div className="freud-card-elevated p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-chocolate-700 mb-1">Program Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. New Model NLP Pre-Training"
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-chocolate-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this program about?"
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 resize-none"
            />
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isSequential}
                onChange={e => setIsSequential(e.target.checked)}
                className="w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-400"
              />
              <span className="text-sm text-chocolate-700">Sequential completion (lock activities until previous is done)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isTemplate}
                onChange={e => setIsTemplate(e.target.checked)}
                className="w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-400"
              />
              <span className="text-sm text-chocolate-700">Save as reusable template</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={e => setIsPublished(e.target.checked)}
                className="w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-400"
              />
              <span className="text-sm text-chocolate-700">Published (visible to assignees)</span>
            </label>
          </div>
        </div>

        {/* Activities */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="freud-heading-base text-chocolate-900">Activities ({activities.length})</h2>
            <button
              onClick={handleAddActivity}
              disabled={saving}
              className="freud-btn-secondary inline-flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Activity
            </button>
          </div>

          {activities.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-chocolate-400 mb-4">No activities yet. Add your first activity to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity, index) => (
                <div key={activity.id} className="flex items-start gap-3 p-4 rounded-lg border border-cream-200 bg-cream-50">
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleMoveActivity(index, 'up')}
                      disabled={index === 0}
                      className="text-chocolate-300 hover:text-chocolate-600 disabled:opacity-30 text-xs"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleMoveActivity(index, 'down')}
                      disabled={index === activities.length - 1}
                      className="text-chocolate-300 hover:text-chocolate-600 disabled:opacity-30 text-xs"
                    >
                      ▼
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-chocolate-400">{index + 1}.</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-sage-100 text-sage-700 font-medium">
                        {ACTIVITY_TYPE_LABELS[activity.activityType]}
                      </span>
                      {activity.dueDate && (
                        <span className="text-xs text-warm-600">Due: {new Date(activity.dueDate).toLocaleDateString()}</span>
                      )}
                    </div>
                    <h4 className="text-sm font-medium text-chocolate-900">{activity.title}</h4>
                    {activity.instructions && (
                      <p className="text-xs text-chocolate-500 mt-1 line-clamp-2">{activity.instructions}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleEditActivity(activity, index)}
                      className="p-1.5 text-chocolate-400 hover:bg-cream-200 rounded"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteActivity(activity.id, index)}
                      className="p-1.5 text-chocolate-400 hover:bg-warm-50 hover:text-warm-600 rounded"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current Assignments */}
        {programId && currentAssignments.length > 0 && (
          <div className="freud-card-elevated p-6">
            <h2 className="freud-heading-base text-chocolate-900 mb-4">Assigned To ({currentAssignments.length})</h2>
            <div className="space-y-2">
              {currentAssignments.map(a => (
                <div key={a.assigneeUserId} className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center text-xs font-bold">
                    {a.assigneeName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-chocolate-900 font-medium">{a.assigneeName}</span>
                  <span className="text-chocolate-400">{a.assigneeEmail}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Activity Editor Modal */}
      {editingActivity && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-cream-200">
              <h3 className="freud-heading-base text-chocolate-900">
                {editingIndex >= 0 ? 'Edit Activity' : 'New Activity'}
              </h3>
              <button onClick={() => { setEditingActivity(null); setEditingIndex(-1); }} className="text-chocolate-400 hover:text-chocolate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Activity Type</label>
                <select
                  value={editingActivity.activityType}
                  onChange={e => setEditingActivity({ ...editingActivity, activityType: e.target.value as ActivityType })}
                  className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 focus:outline-none focus:ring-2 focus:ring-sage-400"
                >
                  {ACTIVITY_TYPES.map(t => (
                    <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editingActivity.title || ''}
                  onChange={e => setEditingActivity({ ...editingActivity, title: e.target.value })}
                  placeholder="e.g. Watch Introduction Video"
                  className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Instructions</label>
                <textarea
                  value={editingActivity.instructions || ''}
                  onChange={e => setEditingActivity({ ...editingActivity, instructions: e.target.value })}
                  placeholder="What should the recipient do?"
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 resize-none"
                />
              </div>
              {['watch', 'listen', 'external_link', 'read'].includes(editingActivity.activityType || '') && (
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Resource URL</label>
                  <input
                    type="url"
                    value={editingActivity.resourceUrl || ''}
                    onChange={e => setEditingActivity({ ...editingActivity, resourceUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Due Date (optional)</label>
                <input
                  type="datetime-local"
                  value={editingActivity.dueDate ? editingActivity.dueDate.slice(0, 16) : ''}
                  onChange={e => setEditingActivity({ ...editingActivity, dueDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  className="w-full px-4 py-2.5 rounded-lg border border-cream-300 bg-white text-chocolate-900 focus:outline-none focus:ring-2 focus:ring-sage-400"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-cream-200">
              <button
                onClick={() => { setEditingActivity(null); setEditingIndex(-1); }}
                className="px-4 py-2 text-sm text-chocolate-600 hover:bg-cream-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveActivity}
                className="freud-btn-primary inline-flex items-center gap-2 text-sm"
              >
                <Check className="w-4 h-4" />
                Save Activity
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-cream-200">
              <h3 className="freud-heading-base text-chocolate-900">
                Assign to {creatorType === 'coach' ? 'Clients' : 'Coaches'}
              </h3>
              <button onClick={() => setShowAssignModal(false)} className="text-chocolate-400 hover:text-chocolate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              {assignees.length === 0 ? (
                <p className="text-sm text-chocolate-400 text-center py-8">
                  No {creatorType === 'coach' ? 'clients' : 'coaches'} available to assign.
                </p>
              ) : (
                <div className="space-y-2">
                  {assignees.map(a => (
                    <label key={a.userId} className="flex items-center gap-3 p-3 rounded-lg hover:bg-cream-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedAssignees.has(a.userId)}
                        onChange={() => handleToggleAssignee(a.userId)}
                        className="w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-400"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-chocolate-900">{a.name}</div>
                        <div className="text-xs text-chocolate-400">{a.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-cream-200">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-sm text-chocolate-600 hover:bg-cream-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAssignments}
                className="freud-btn-primary inline-flex items-center gap-2 text-sm"
              >
                <Check className="w-4 h-4" />
                Save Assignments
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
